import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'preact/hooks';

import { getProductsQueryOptions, postUserProductMutationFn } from '@/queries/products';
import { ProductInterval } from '@/queries/products/types';
import { trackMP } from '@/shared/mp';

import { Button } from '../Buttons';
import { ProductFeaturesList, ProductFeaturesListItem } from './ProductFeatureList';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';
import { parseBenefits } from './utils';

interface EducatorSubscriptionProps {
  entryPoint?: string; // for analytics only
}

const EducatorSubscription = ({ entryPoint }: EducatorSubscriptionProps) => {
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');

  const { data: products } = useQuery({
    ...getProductsQueryOptions({
      lookupKey: 'monthly-educator,annual-educator',
    }),
    select: (data) => data.list,
  });

  const monthlyProduct = products?.find((product) => product.productLookupKey === 'monthly-educator');
  const yearlyProduct = products?.find((product) => product.productLookupKey === 'annual-educator');

  const product = productInterval === 'year' ? yearlyProduct : monthlyProduct;

  const benefits = useMemo(() => parseBenefits(product), [product]);

  const { mutate: checkout, isPending } = useMutation({
    mutationFn: postUserProductMutationFn,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  return (
    <div className="flex w-full max-w-[336px] flex-col items-center justify-center rounded-2xl bg-beige-light shadow-[0px_4px_12px_0px_rgba(0,0,0,0.20)]">
      <div className="mt-5 flex w-full flex-col items-center gap-4">
        <div className="mx-4 w-full max-w-[284px]">
          <h4 className="preflight preflight-h4 text-2xl/7 font-bold text-green-900">Educator Membership</h4>
          <p className="preflight preflight-p text-sm text-stone-500">Unlimited learning on top of existing access.</p>
        </div>
        <div className="mx-4 flex w-full max-w-[284px] items-center justify-between gap-2">
          <ProductPrice currency={product?.price.currency} price={product?.price.price} />
          <ProductIntervalSelector value={productInterval} onChange={setProductInterval} className="bg-white" />
        </div>
        <ProductFeaturesList className="w-full px-4">
          {benefits.map((benefit, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <ProductFeaturesListItem key={index.toString()}>{benefit}</ProductFeaturesListItem>
          ))}
        </ProductFeaturesList>
      </div>
      <div className="w-full px-4 pb-4 pt-3">
        <div className="px-4">
          <Button
            disabled={!product || isPending}
            size="manual"
            className="w-full px-4 py-3 text-base/none font-bold"
            onClick={() => {
              if (!product) return;
              checkout({
                productId: product._id,
                priceId: product.price.priceId,
              });
              trackMP('request-checkout', {
                productId: product._id,
                priceId: product.price.priceId,
                productLookupKey: product.productLookupKey,
                entryPoint,
              });
            }}
          >
            Select Membership
          </Button>
        </div>

        <p className="preflight preflight-p mt-3 text-center text-sm text-stone-500">Cancel Anytime</p>
      </div>
    </div>
  );
};

export default EducatorSubscription;
